import type { RefinementCtx } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

type OfferBasics = {
  dual_education?: boolean;
  employment_types: string;
  categories?: unknown;
  min_salary: string;
  max_salary: string;
  free_places?: string;
  date_start?: unknown;
  start_immediately?: boolean;
};

/** Only validate employment controls that are actually visible in this offer mode. */
export function validateJobOfferBasics(
  data: OfferBasics,
  context: RefinementCtx,
  VM: FormValidationMessages,
) {
  const issue = (field: string, message: string) =>
    context.addIssue({ code: 'custom', path: [field], message });
  if (!data.dual_education && !/^[1-9]\d*$/.test(data.employment_types))
    issue('employment_types', VM.REQUIRED_FIELD_VALIDATION_MESSAGE());
  if (!Array.isArray(data.categories) || !data.categories.length)
    issue('categories', VM.REQUIRED_FIELD_VALIDATION_MESSAGE());
  else if (data.categories.length > 3) issue('categories', VM.MAX_LENGTH_VALIDATION_MESSAGE(3));
  else if (
    data.categories.some(
      category => !Number.isSafeInteger(Number(category?.value)) || Number(category.value) <= 0,
    )
  )
    issue('categories', VM.REQUIRED_FIELD_VALIDATION_MESSAGE());
  if (!data.dual_education) {
    for (const field of ['min_salary', 'max_salary'] as const) {
      const value = data[field].trim().replace(',', '.');
      if (value && (!/^\d+(\.\d+)?$/.test(value) || !Number.isFinite(Number(value))))
        issue(field, VM.INVALID_NUMBER_VALIDATION_MESSAGE());
    }
  }
  if (data.free_places !== undefined && !/^\d+$/.test(data.free_places))
    issue('free_places', VM.INVALID_INTEGER_VALIDATION_MESSAGE());
  if (
    data.date_start !== undefined &&
    data.date_start !== null &&
    data.date_start !== '' &&
    (!(data.date_start instanceof Date) || !Number.isFinite(data.date_start.getTime()))
  )
    issue('date_start', VM.INVALID_DATE_VALIDATION_MESSAGE());
  if (
    (data.date_start === undefined || data.date_start === null || data.date_start === '') &&
    !data.start_immediately
  )
    issue('date_start', VM.REQUIRED_FIELD_VALIDATION_MESSAGE());
}

export function getOfferErrorField(field: string): string {
  const root = field.split('.')[0];
  return (
    (
      { employment_type: 'employment_types', company_contact_url: 'video' } as Record<
        string,
        string
      >
    )[root] ?? root
  );
}

export function getOfferErrorStep(
  errors: unknown,
  buckets: Record<number, readonly string[]>,
): 0 | 1 | 2 | 3 | 4 {
  if (!errors || typeof errors !== 'object') return 0;
  const fields = Object.keys(errors).map(getOfferErrorField);
  const match = Object.entries(buckets).find(([, names]) =>
    names.some(name => fields.includes(name)),
  );
  return match ? (Number(match[0]) as 0 | 1 | 2 | 3 | 4) : 0;
}
